import { useCallback, useRef, useState } from 'react'
import { attachments as api, uploadToBucket } from '@/api/endpoints'
import type { AttachmentKind } from '@/api/types'
import { prepareForUpload } from './compress'
import { errorMessage } from '@/api/messages'

export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024
export const ACCEPTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'application/pdf',
]

export interface PendingUpload {
  id: string
  filename: string
  contentType: string
  /** A local object URL so the doctor sees the photo before the network has
   *  done anything at all. */
  previewUrl: string | null
  progress: number
  /** `queued` means the visit does not exist yet: the file is held locally and
   *  goes up the moment the visit is saved. */
  status: 'queued' | 'preparing' | 'uploading' | 'completing' | 'done' | 'failed'
  error: string | null
  attachmentId: string | null
  /** Kept for queued files and for retrying a failure. */
  blob: Blob
  kind: AttachmentKind
}

let counter = 0

function makePreviewUrl(blob: Blob, contentType: string): string | null {
  if (!contentType.startsWith('image/')) return null
  try {
    return URL.createObjectURL(blob)
  } catch {
    return null
  }
}

export function useUploader(visitId: string | null) {
  const [uploads, setUploads] = useState<PendingUpload[]>([])
  // A ref, not state: flushQueued is called from a save handler that must not
  // depend on a re-render having happened first.
  const queued = useRef<PendingUpload[]>([])

  const patch = useCallback((id: string, changes: Partial<PendingUpload>) => {
    setUploads((current) => current.map((u) => (u.id === id ? { ...u, ...changes } : u)))
  }, [])

  const send = useCallback(
    async (id: string, targetVisitId: string, blob: Blob, filename: string, contentType: string, kind: AttachmentKind) => {
      try {
        const prepared = await prepareForUpload(blob, filename, contentType)
        if (!ACCEPTED_TYPES.includes(prepared.contentType)) {
          patch(id, { status: 'failed', error: 'That file type cannot be attached.' })
          return
        }
        if (prepared.blob.size > MAX_UPLOAD_BYTES) {
          patch(id, { status: 'failed', error: 'This file is too large to upload here.' })
          return
        }

        patch(id, { status: 'uploading' })
        const ticket = await api.createTicket(targetVisitId, {
          original_filename: prepared.filename,
          content_type: prepared.contentType,
          byte_size: prepared.blob.size,
          kind,
        })
        patch(id, { attachmentId: ticket.attachment_id })

        await uploadToBucket(ticket, prepared.blob, prepared.filename, (fraction) =>
          patch(id, { progress: fraction }),
        )

        patch(id, { status: 'completing', progress: 1 })
        await api.complete(ticket.attachment_id)
        patch(id, { status: 'done' })
      } catch (error) {
        // The failed upload stays in the list with its file still referenced, so
        // it can be retried. Letting it disappear would leave the doctor
        // believing the X-ray is in the record when it is not.
        patch(id, {
          status: 'failed',
          // Never a raw thrown message: "Failed to fetch" is not something
          // to put in front of someone holding an X-ray.
          error: errorMessage(error),
        })
      }
    },
    [patch],
  )

  const upload = useCallback(
    async (blob: Blob, filename: string, contentType: string, kind: AttachmentKind) => {
      const id = `u${++counter}`
      // A thumbnail is a nicety; the upload is the point. If the runtime cannot
      // make an object URL, carry on without a preview rather than dropping the
      // file on the floor.
      const previewUrl = makePreviewUrl(blob, contentType)
      setUploads((current) => [
        ...current,
        {
          id,
          filename,
          contentType,
          previewUrl,
          progress: 0,
          // A brand-new visit has no id yet. The file waits rather than being
          // refused: the doctor photographs the X-ray while the patient is in
          // front of them, not after saving and reopening the record.
          status: visitId ? 'preparing' : 'queued',
          error: null,
          attachmentId: null,
          blob,
          kind,
        },
      ])

      if (!visitId) return
      await send(id, visitId, blob, filename, contentType, kind)
    },
    [visitId, send],
  )

  /** Called once the visit exists. Sends everything that was waiting for it. */
  const flushQueued = useCallback(
    async (newVisitId: string) => {
      const waiting = queued.current
      queued.current = []
      await Promise.all(
        waiting.map((item) =>
          send(item.id, newVisitId, item.blob, item.filename, item.contentType, item.kind),
        ),
      )
    },
    [send],
  )

  const remove = useCallback((id: string) => {
    setUploads((current) => {
      const target = current.find((u) => u.id === id)
      if (target?.previewUrl) {
        try {
          URL.revokeObjectURL(target.previewUrl)
        } catch {
          // Nothing to reclaim if the runtime never made one.
        }
      }
      return current.filter((u) => u.id !== id)
    })
  }, [])

  // Keep the ref in step with what is waiting.
  queued.current = uploads.filter((u) => u.status === 'queued')

  const inFlight = uploads.some((u) => u.status === 'uploading' || u.status === 'completing')

  return { uploads, upload, remove, flushQueued, inFlight, queuedCount: queued.current.length }
}
